/**
 * Campaign Draft Manager
 * Handles localStorage operations for campaign drafts with consistent key naming and cleanup
 */

// Storage keys
export const STORAGE_KEYS = {
  CREATE_CAMPAIGN: 'create-campaign',
  EDIT_CAMPAIGN: (id) => `edit-campaign-${id}`,
};

// Maximum storage quota in bytes (5MB default)
const MAX_STORAGE_SIZE = 5 * 1024 * 1024;

/**
 * Get storage usage and check if within limits
 */
export const getStorageInfo = () => {
  let totalSize = 0;
  const draftKeys = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key === STORAGE_KEYS.CREATE_CAMPAIGN || key.startsWith('edit-campaign-'))) {
        const value = localStorage.getItem(key);
        totalSize += (key.length + (value ? value.length : 0)) * 2; // UTF-16 encoding
        draftKeys.push(key);
      }
    }
  } catch (error) {
    console.warn('Error calculating storage usage:', error);
  }

  return {
    totalSize,
    draftKeys,
    isNearLimit: totalSize > MAX_STORAGE_SIZE * 0.8,
    isOverLimit: totalSize > MAX_STORAGE_SIZE,
  };
};

/**
 * Save campaign draft to localStorage
 */
export const saveCampaignDraft = (key, draftData) => {
  console.log('saveCampaignDraft called with:', { key, draftData });

  try {
    // Clean data before saving (remove unnecessary properties)
    const cleanData = {
      campaignName: draftData.campaignName || '',
      description: draftData.description || '',
      steps: draftData.steps || [],
      lastModified: new Date().toISOString(),
    };

    console.log('Clean data prepared:', cleanData);
    const dataString = JSON.stringify(cleanData);
    console.log('Data string length:', dataString.length);

    // Check storage before saving
    const storageInfo = getStorageInfo();
    const newSize = (key.length + dataString.length) * 2;

    console.log('Storage info:', {
      currentSize: storageInfo.totalSize,
      newSize,
      totalAfterSave: storageInfo.totalSize + newSize,
      maxSize: MAX_STORAGE_SIZE,
    });

    if (storageInfo.totalSize + newSize > MAX_STORAGE_SIZE) {
      console.warn('Storage quota exceeded. Consider cleaning up old drafts.');
      return false;
    }

    localStorage.setItem(key, dataString);
    console.log('Successfully saved to localStorage with key:', key);
    return true;
  } catch (error) {
    console.error('Error saving campaign draft:', error);
    return false;
  }
};

/**
 * Load campaign draft from localStorage
 */
export const loadCampaignDraft = (key) => {
  try {
    const draftData = localStorage.getItem(key);
    if (draftData) {
      return JSON.parse(draftData);
    }
    return null;
  } catch (error) {
    console.error('Error loading campaign draft:', error);
    return null;
  }
};

/**
 * Remove campaign draft from localStorage
 */
export const removeCampaignDraft = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing campaign draft:', error);
    return false;
  }
};

/**
 * Check if a campaign draft exists
 */
export const draftExists = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error('Error checking draft existence:', error);
    return false;
  }
};

/**
 * Get create campaign draft key
 */
export const getCreateDraftKey = () => STORAGE_KEYS.CREATE_CAMPAIGN;

/**
 * Get edit campaign draft key for specific ID
 */
export const getEditDraftKey = (campaignId) => STORAGE_KEYS.EDIT_CAMPAIGN(campaignId);

/**
 * Clean up all campaign drafts (emergency cleanup)
 */
export const cleanupAllDrafts = () => {
  try {
    const storageInfo = getStorageInfo();
    storageInfo.draftKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
    return true;
  } catch (error) {
    console.error('Error cleaning up drafts:', error);
    return false;
  }
};

/**
 * Clean up old drafts (older than specified days)
 */
export const cleanupOldDrafts = (daysOld = 7) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const storageInfo = getStorageInfo();

    storageInfo.draftKeys.forEach((key) => {
      const draft = loadCampaignDraft(key);
      if (draft && draft.lastModified) {
        const draftDate = new Date(draft.lastModified);
        if (draftDate < cutoffDate) {
          removeCampaignDraft(key);
        }
      }
    });

    return true;
  } catch (error) {
    console.error('Error cleaning up old drafts:', error);
    return false;
  }
};

/**
 * Auto-save functionality with debouncing
 */
let autoSaveTimeout = null;
export const autoSaveDraft = (key, draftData, delay = 100) => {
  console.log('autoSaveDraft called:', { key, delay });

  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }

  autoSaveTimeout = setTimeout(() => {
    console.log('Auto-save timeout triggered, saving to:', key);
    const success = saveCampaignDraft(key, draftData);
    console.log('Save result:', success);

    // Log current localStorage state
    try {
      const savedData = localStorage.getItem(key);
      console.log('Data in localStorage after save:', savedData ? JSON.parse(savedData) : null);
    } catch (e) {
      console.error('Error reading saved data:', e);
    }
  }, delay);
};

/**
 * Validate draft data before recovery
 */
export const validateDraftData = (draftData) => {
  if (!draftData || typeof draftData !== 'object') {
    return false;
  }

  // Check required structure
  const hasValidStructure =
    typeof draftData.campaignName === 'string' &&
    typeof draftData.description === 'string' &&
    Array.isArray(draftData.steps);

  if (!hasValidStructure) {
    return false;
  }

  // Validate steps structure
  const validSteps = draftData.steps.every(
    (step) =>
      step &&
      typeof step.id !== 'undefined' &&
      typeof step.type === 'string' &&
      (step.type === 'action' || step.type === 'delay')
  );

  return validSteps;
};
